// src/pages/Admin/components/EventsEditor.jsx
// Upravená verze se správným zpracováním data a typem události

import { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';

const EventsEditor = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentEvent, setCurrentEvent] = useState({
    id: null,
    date: '',
    image_path: '',
    event_url: '',
    is_active: true,
    event_type: 'our', // Výchozí typ události je "naše"
    translations: {
      en: { title: '', location: '', description: '' },
      cs: { title: '', location: '', description: '' },
    },
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const language = getUserLanguage();
  const IMAGE_BASE_URL = 'https://www.norisknodrift.com';

  const getImageUrl = (path) => {
    if (!path) {
      console.log('[EventsEditor] No image path provided, using placeholder');
      return '/images/events/placeholder.jpg';
    }
    const normalizedPath = path.startsWith('/') ? path : `/images/events/${path}`;
    const fullUrl = `${IMAGE_BASE_URL}${normalizedPath}`;
    console.log('[EventsEditor] Generated image URL:', fullUrl);
    return fullUrl;
  };

  // Převede jakýkoliv formát data na YYYY-MM-DD
  const formatDateString = (dateValue) => {
    if (!dateValue) return '';
    
    let dateObj;
    if (typeof dateValue === 'string') {
      // Pokud je datum ISO string (2025-05-30T00:00:00.000Z) nebo již YYYY-MM-DD
      if (dateValue.includes('T')) {
        dateValue = dateValue.split('T')[0];
      }
      
      // Pokud již máme správný formát YYYY-MM-DD, vrátíme ho přímo
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      
      // Jinak převedeme na Date objekt
      dateObj = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      dateObj = dateValue;
    } else {
      return '';
    }
    
    // Kontrola, zda je datum platné
    if (isNaN(dateObj.getTime())) {
      console.error('[EventsEditor] Invalid date provided:', dateValue);
      return '';
    }
    
    // Formátování na YYYY-MM-DD
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    console.log('[EventsEditor] Fetching events...');
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get('/events');
      console.log('[EventsEditor] Events fetched:', data);
      const processedData = (data || []).map(event => ({
        ...event,
        date: formatDateString(event.date),
        is_active: !!event.is_active,
        event_type: event.event_type || 'our',
        image_path: event.image_path || '',
        translations: event.translations || {
          en: { title: '', location: '', description: '' },
          cs: { title: '', location: '', description: '' },
        },
      }));

      processedData.forEach(event => {
        console.log(`[EventsEditor] Event ${event.id} processed date:`, event.date);
      });

      setEvents(processedData);
    } catch (err) {
      console.error('[EventsEditor] Error fetching events:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst události' : 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, lang = language) => {
    const { name, value, type, checked } = e.target;
    console.log('[EventsEditor] Handling change:', { name, value, lang, type, checked });
    if (['title', 'location', 'description'].includes(name)) {
      setCurrentEvent(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            [name]: value,
          },
        },
      }));
    } else {
      setCurrentEvent(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedImage(e.target.files[0]);
      console.log('[EventsEditor] Selected image:', e.target.files[0].name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    console.log('[EventsEditor] Submitting form:', { 
      editMode, 
      currentDate: currentEvent.date,
      hasImage: !!selectedImage || (editMode && currentEvent.image_path)
    });

    // Validace data pouze při vytváření nové události
    if (!editMode && (!currentEvent.date || !/^\d{4}-\d{2}-\d{2}$/.test(currentEvent.date))) {
      setError(language === 'cs' ? 'Datum je povinné a musí být ve formátu YYYY-MM-DD' : 'Date is required and must be in YYYY-MM-DD format');
      console.error('[EventsEditor] Validation failed: Invalid date format:', currentEvent.date);
      return;
    }

    if (!currentEvent.translations.en.title || !currentEvent.translations.cs.title) {
      setError(language === 'cs' ? 'Název je povinný v obou jazycích' : 'Title is required in both languages');
      console.error('[EventsEditor] Validation failed: Title required');
      return;
    }

    if (!editMode && !selectedImage) {
      setError(language === 'cs' ? 'Fotografie je povinná pro novou událost' : 'Photo is required for new event');
      console.error('[EventsEditor] Validation failed: Image required for new event');
      return;
    }

    try {
      const formData = new FormData();
      if (selectedImage) {
        formData.append('image', selectedImage);
        console.log('[EventsEditor] Appending new image:', selectedImage.name);
      } else if (editMode && currentEvent.image_path) {
        formData.append('image_path', currentEvent.image_path);
        console.log('[EventsEditor] Appending existing image_path:', currentEvent.image_path);
      }
      
      // Zajistíme, že datum je ve správném formátu
      const formattedDate = formatDateString(currentEvent.date);
      formData.append('date', formattedDate);
      console.log('[EventsEditor] Date being submitted:', formattedDate);
      
      formData.append('event_url', currentEvent.event_url || '#');
      formData.append('is_active', currentEvent.is_active ? 'true' : 'false');
      formData.append('event_type', currentEvent.event_type || 'our');
      formData.append('translations', JSON.stringify(currentEvent.translations));

      console.log('[EventsEditor] Form data prepared for submission');

      let response;
      if (editMode) {
        console.log('[EventsEditor] Updating event ID:', currentEvent.id);
        response = await put(`/events/${currentEvent.id}`, formData);
        setSuccess(language === 'cs' ? 'Událost úspěšně aktualizována' : 'Event updated successfully');
      } else {
        console.log('[EventsEditor] Creating new event');
        response = await post('/events', formData);
        setSuccess(language === 'cs' ? 'Událost úspěšně přidána' : 'Event added successfully');
      }

      console.log('[EventsEditor] Submit response received');
      resetForm();
      fetchEvents();
    } catch (err) {
      console.error('[EventsEditor] Error saving event:', err);
      setError(language === 'cs' ? `Nepodařilo se uložit událost: ${err.message}` : `Failed to save event: ${err.message}`);
    }
  };

  const handleEdit = async (id) => {
    console.log('[EventsEditor] Editing event ID:', id);
    try {
      const event = await get(`/events/${id}`);
      console.log('[EventsEditor] Event fetched for edit:', event);
      
      // Formátování data
      const formattedDate = formatDateString(event.date);
      console.log('[EventsEditor] Original date:', event.date, 'Formatted date:', formattedDate);
      
      setCurrentEvent({
        id: event.id,
        date: formattedDate,
        image_path: event.image_path || '',
        event_url: event.event_url || '',
        is_active: !!event.is_active,
        event_type: event.event_type || 'our',
        translations: event.translations || {
          en: { title: '', location: '', description: '' },
          cs: { title: '', location: '', description: '' },
        },
      });

      setEditMode(true);
      setSelectedImage(null);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[EventsEditor] Error fetching event for edit:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst událost pro úpravu' : 'Failed to load event for editing');
    }
  };

  const handleDelete = async (id) => {
    console.log('[EventsEditor] Deleting event ID:', id);
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tuto událost?' : 'Are you sure you want to delete this event?')) {
      return;
    }
    try {
      await del(`/events/${id}`);
      setSuccess(language === 'cs' ? 'Událost úspěšně smazána' : 'Event deleted successfully');
      fetchEvents();
    } catch (err) {
      console.error('[EventsEditor] Error deleting event:', err);
      setError(language === 'cs' ? 'Nepodařilo se smazat událost' : 'Failed to delete event');
    }
  };

  const resetForm = () => {
    console.log('[EventsEditor] Resetting form');
    setCurrentEvent({
      id: null,
      date: '',
      image_path: '',
      event_url: '',
      is_active: true,
      event_type: 'our', // Resetování na výchozí typ
      translations: {
        en: { title: '', location: '', description: '' },
        cs: { title: '', location: '', description: '' },
      },
    });
    setSelectedImage(null);
    setEditMode(false);
    setShowForm(false);
    setError(null);
    setSuccess(null);
  };

  const handleAddNew = () => {
    console.log('[EventsEditor] Opening form for new event');
    resetForm();
    setShowForm(true);
  };

  const getEventTypeLabel = (type) => {
    if (type === 'our') {
      return language === 'cs' ? 'Naše událost' : 'Our Event';
    } else if (type === 'world') {
      return language === 'cs' ? 'Událost ve světě' : 'World Event';
    }
    return language === 'cs' ? 'Neznámý typ' : 'Unknown Type';
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">{language === 'cs' ? 'Správa událostí' : 'Events Management'}</h1>
        <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
          {language === 'cs' ? 'Přidat událost' : 'Add Event'}
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode ? (language === 'cs' ? 'Upravit událost' : 'Edit Event') : (language === 'cs' ? 'Přidat událost' : 'Add Event')}
            </h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">English</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-form-input"
                      value={currentEvent.translations.en.title || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Location</label>
                    <input
                      type="text"
                      name="location"
                      className="admin-form-input"
                      value={currentEvent.translations.en.location || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Description</label>
                    <textarea
                      name="description"
                      className="admin-form-textarea"
                      value={currentEvent.translations.en.description || ''}
                      onChange={(e) => handleChange(e, 'en')}
                      rows="4"
                    />
                  </div>

                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">Čeština</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Název</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-form-input"
                      value={currentEvent.translations.cs.title || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Místo</label>
                    <input
                      type="text"
                      name="location"
                      className="admin-form-input"
                      value={currentEvent.translations.cs.location || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Popis</label>
                    <textarea
                      name="description"
                      className="admin-form-textarea"
                      value={currentEvent.translations.cs.description || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                      rows="4"
                    />
                  </div>
                </div>

                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Fotografie' : 'Photo'}</label>
                    <input
                      type="file"
                      name="image"
                      className="admin-form-input"
                      onChange={handleImageChange}
                      accept="image/*"
                      required={!editMode}
                    />
                    {editMode && !selectedImage && currentEvent.image_path && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Aktuální obrázek:' : 'Current image:'}</p>
                        <p className="admin-text-xs admin-text-gray-400">{currentEvent.image_path}</p>
                        <img
                          src={getImageUrl(currentEvent.image_path)}
                          alt={currentEvent.translations[language]?.title || 'Event'}
                          className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                          onError={(e) => {
                            console.error('[EventsEditor] Image load error:', currentEvent.image_path);
                            e.target.onerror = null;
                            e.target.src = '/images/events/placeholder.jpg';
                          }}
                        />
                      </div>
                    )}
                    {selectedImage && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Vybraný obrázek:' : 'Selected image:'} {selectedImage.name}</p>
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="Preview"
                          className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Vylepšené zpracování data */}
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Datum' : 'Date'}</label>
                    <input
                      type="date"
                      name="date"
                      className="admin-form-input"
                      value={currentEvent.date || ''}
                      onChange={handleChange}
                    />
                    {editMode && currentEvent.date && (
                      <p className="admin-text-xs admin-text-gray-400 admin-mt-1">
                        {language === 'cs' 
                          ? `Aktuální datum: ${currentEvent.date}`
                          : `Current date: ${currentEvent.date}`}
                      </p>
                    )}
                  </div>
                  
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'URL události' : 'Event URL'}</label>
                    <input
                      type="url"
                      name="event_url"
                      className="admin-form-input"
                      value={currentEvent.event_url || ''}
                      onChange={handleChange}
                    />
                  </div>
                  
                  {/* Výběr typu události */}
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Typ události' : 'Event Type'}</label>
                    <select
                      name="event_type"
                      className="admin-form-input"
                      value={currentEvent.event_type || 'our'}
                      onChange={handleChange}
                    >
                      <option value="our">{language === 'cs' ? 'Naše událost' : 'Our Event'}</option>
                      <option value="world">{language === 'cs' ? 'Událost ve světě' : 'World Event'}</option>
                    </select>
                    <p className="admin-text-xs admin-text-gray-400">
                      {language === 'cs' 
                        ? 'Naše události: závody a akce, kterých se účastní náš tým. Události ve světě: ostatní významné akce.'
                        : 'Our events: races and events our team participates in. World events: other significant events.'}
                    </p>
                  </div>
                  
                  <div className="admin-mb-4">
                  <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        className="admin-form-checkbox"
                        checked={!!currentEvent.is_active}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Aktivní' : 'Active'}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="admin-mt-6 admin-flex admin-items-center">
                <button type="submit" className="admin-btn admin-btn-primary admin-mr-2">
                  {editMode ? (language === 'cs' ? 'Aktualizovat' : 'Update') : (language === 'cs' ? 'Přidat' : 'Add')}
                </button>
                <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">{language === 'cs' ? 'Seznam událostí' : 'Events List'}</div>
        {events.length === 0 ? (
          <div className="admin-p-4 admin-text-center admin-text-gray-300">{language === 'cs' ? 'Žádné události nebyly nalezeny.' : 'No events found.'}</div>
        ) : (
          <div className="admin-overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr className="admin-bg-accent-gray">
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Název' : 'Title'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Datum' : 'Date'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Místo' : 'Location'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Typ' : 'Type'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Aktivní' : 'Active'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  const eventData = event.translations && event.translations[language]
                    ? event.translations[language]
                    : (event.translations && event.translations.en
                      ? event.translations.en
                      : { title: '', location: '', description: '' });
                  return (
                    <tr key={event.id} className="admin-table-row">
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-flex admin-items-center">
                          <div className="admin-flex-shrink-0 admin-h-10 admin-w-10">
                            <img
                              className="admin-h-10 admin-w-10 admin-rounded-full admin-object-cover"
                              src={getImageUrl(event.image_path)}
                              alt={eventData.title || 'Event'}
                              onError={(e) => {
                                console.error('[EventsEditor] Table image load error:', event.image_path);
                                e.target.onerror = null;
                                e.target.src = '/images/events/placeholder.jpg';
                              }}
                            />
                          </div>
                          <div className="admin-ml-4">
                            <div className="admin-text-sm admin-font-medium admin-text-white">
                              {eventData.title || (language === 'cs' ? 'Bezejmenná událost' : 'Unnamed Event')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">{event.date || (language === 'cs' ? 'Není uvedeno' : 'Not specified')}</div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">
                          {eventData.location || (language === 'cs' ? 'Není uvedeno místo' : 'Location not specified')}
                        </div>
                      </td>
                      {/* Sloupec pro typ události */}
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <span className={`admin-badge ${event.event_type === 'our' ? 'admin-badge-blue' : 'admin-badge-purple'}`}>
                          {getEventTypeLabel(event.event_type)}
                        </span>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <span className={`admin-badge ${event.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                          {event.is_active ? (language === 'cs' ? 'Ano' : 'Yes') : (language === 'cs' ? 'Ne' : 'No')}
                        </span>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                        <button onClick={() => handleEdit(event.id)} className="admin-text-blue-600 hover:admin-text-blue-900 admin-mr-3">
                          {language === 'cs' ? 'Upravit' : 'Edit'}
                        </button>
                        <button onClick={() => handleDelete(event.id)} className="admin-text-red-600 hover:admin-text-red-900">
                          {language === 'cs' ? 'Smazat' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsEditor;